// src/components/JobStatusMonitor.tsx

import axios from "axios";
import { useCallback, useEffect, useRef } from "react";
import { safeStringify } from "@/utils/api";

interface JobStatusMonitorProps {
  jobId: string;
  originalStatus: string;
  onStatusUpdate: (status: string) => void;
  onError: (error: Error) => void;
}

const MAX_RETRIES = 3;
const POLLING_INTERVAL = 5000;

const JobStatusMonitor: React.FC<JobStatusMonitorProps> = ({
  jobId,
  originalStatus,
  onStatusUpdate,
  onError,
}) => {
  const timeoutsRef = useRef<number[]>([]);

  useEffect(() => {
    if (typeof onStatusUpdate !== "function") {
      console.error("JobStatusMonitor: onStatusUpdate must be a function");
      return;
    }
    if (typeof onError !== "function") {
      console.error("JobStatusMonitor: onError must be a function");
      return;
    }
  }, [onStatusUpdate, onError]);

  const revertJobStatus = useCallback(
    async (controller: AbortController) => {
      try {
        await axios.post(
          "/api/revert-job-status",
          { jobId, originalStatus },
          { signal: controller.signal },
        );
        onStatusUpdate(originalStatus);
        console.log("Job reverted to previous state due to processing issues");
      } catch (error) {
        if (axios.isCancel(error)) {
          return; // Request was canceled
        }
        console.error("Failed to revert job status:", safeStringify(error));
        onError(error instanceof Error ? error : new Error("Failed to revert job status"));
      }
    },
    [jobId, originalStatus, onStatusUpdate, onError],
  );

  const checkStatus = useCallback(
    async (retryCount: number, controller: AbortController) => {
      try {
        const response = await axios.get(`/api/job_status/${jobId}`, {
          signal: controller.signal,
        });
        const { status, error_message } = response.data;
        const isFailed =
          status === "processing_failed" ||
          status.endsWith("_failed") ||
          status.includes("generation_failed") ||
          status === "failed";

        if (isFailed || error_message) {
          await revertJobStatus(controller);
          throw new Error(error_message || "Job processing failed");
        }

        if (status === "completed") {
          onStatusUpdate("completed");
          return;
        }

        if (status === "processing_additional" && retryCount < MAX_RETRIES) {
          const timeoutId = window.setTimeout(
            () => checkStatus(retryCount + 1, controller),
            POLLING_INTERVAL,
          );
          timeoutsRef.current.push(timeoutId);
        } else if (retryCount >= MAX_RETRIES) {
          await revertJobStatus(controller);
          throw new Error("Job processing timed out");
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return; // Request was canceled
        }
        onError(error instanceof Error ? error : new Error("Failed to check job status"));
      }
    },
    [jobId, onStatusUpdate, onError, revertJobStatus],
  );

  useEffect(() => {
    const controller = new AbortController();
    checkStatus(0, controller);

    // Capture the current timeouts at the time the effect runs
    const currentTimeouts = timeoutsRef.current;

    return () => {
      controller.abort(); // Cancel any ongoing Axios requests
      currentTimeouts.forEach((timeoutId) => clearTimeout(timeoutId)); // Clear pending timeouts
    };
  }, [checkStatus]);

  return null; // This is a non-visual component
};

export default JobStatusMonitor;
